import { useCallback, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { cn } from "@/cn";
import { listProviders, updateProvider, type ProviderConfig } from "@/api/providers";
import { useClientPagination } from "@/hooks/usePagination";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { useSyncCursorPage } from "@/hooks/useSyncCursorPage";
import { PaginationBar } from "@/components/pagination-bar";
import {
  CollectionCommandBar,
  DataTable,
  EntityCard,
  EntityCompactItem,
  EntityListLayout,
  useClientTableSort,
  useListViewMode,
  type Column,
  type EntityFilterSelectField,
  type EntitySortOption,
} from "@/components/collection";
import {
  TYPE_LABELS,
  TYPE_COLORS,
  TYPE_FILTER_TABS,
  filterProviders,
  countEnabledProviders,
  isScannableAtsProvider,
  isScannableBoardProvider,
  type ProviderTypeFilter,
  type ProviderStatusFilter,
} from "@/lib/provider-ui";

const ALLOWED_MODES = ["cards", "list", "table"] as const;

const SORT_OPTIONS: EntitySortOption[] = [
  { field: "name", order: "asc", label: "Name A–Z" },
  { field: "type", order: "asc", label: "Type" },
  { field: "status", order: "desc", label: "Enabled first" },
];

function applySort(
  items: ProviderConfig[],
  sort: EntitySortOption,
): ProviderConfig[] {
  const dir = sort.order === "asc" ? 1 : -1;
  return [...items].sort((a, b) => {
    if (sort.field === "type") {
      return a.type.localeCompare(b.type) * dir;
    }
    if (sort.field === "status") {
      return (Number(a.enabled) - Number(b.enabled)) * dir;
    }
    return a.name.localeCompare(b.name) * dir;
  });
}

export function SettingsProvidersPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTab, setFilterTab] = useState<ProviderTypeFilter>("all");
  const [statusFilter, setStatusFilter] = useState<ProviderStatusFilter>("all");
  const [sortOption, setSortOption] = useState<EntitySortOption>(SORT_OPTIONS[0]);

  const { viewMode, setViewMode, allowedModes } = useListViewMode({
    storageKey: "viewMode_settings_providers",
    defaultMode: "cards",
    allowedModes: [...ALLOWED_MODES],
  });

  const useServerCursor =
    !searchQuery.trim() && filterTab === "all" && statusFilter === "all";

  const cursorPag = useCursorPagination([]);

  const {
    data: cursorData,
    isLoading: cursorLoading,
    error: cursorError,
  } = useQuery({
    queryKey: ["providers", "cursor", cursorPag.pageSize, cursorPag.cursor],
    queryFn: () =>
      listProviders({
        limit: cursorPag.pageSize,
        cursor: cursorPag.cursor,
      }),
    enabled: useServerCursor,
    staleTime: 30_000,
  });

  useSyncCursorPage(
    cursorPag.setNextCursor,
    cursorData,
    useServerCursor && !cursorLoading && !!cursorData,
  );

  const {
    data: allData,
    isLoading: allLoading,
    error: allError,
  } = useQuery({
    queryKey: ["providers", "all"],
    queryFn: async () => (await listProviders()).providers,
    enabled: !useServerCursor,
    staleTime: 30_000,
  });

  const isLoading = useServerCursor ? cursorLoading : allLoading;
  const error = useServerCursor ? cursorError : allError;

  const providers = useServerCursor
    ? (cursorData?.providers ?? [])
    : (allData ?? []);

  const filteredProviders = useMemo(() => {
    if (useServerCursor) {
      return applySort(providers, sortOption);
    }
    return applySort(
      filterProviders(providers, {
        search: searchQuery,
        type: filterTab,
        status: statusFilter,
      }),
      sortOption,
    );
  }, [
    useServerCursor,
    providers,
    searchQuery,
    filterTab,
    statusFilter,
    sortOption,
  ]);

  const enabledCount = countEnabledProviders(providers);

  const clientPag = useClientPagination(
    useServerCursor ? [] : filteredProviders,
    [filterTab, searchQuery, statusFilter, sortOption.field],
  );

  const pageItems = useServerCursor
    ? filteredProviders
    : clientPag.pageItems;

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      updateProvider(id, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["providers"] }),
  });

  const getSortValue = useCallback((p: ProviderConfig, key: string) => {
    if (key === "type") return p.type;
    if (key === "status") return p.enabled ? 1 : 0;
    return p.name;
  }, []);

  const { sortedItems, sortKey, sortDirection, onSortChange } =
    useClientTableSort({
      items: pageItems.map((p) => ({ ...p, id: p.providerId })),
      defaultKey: null,
      getValue: getSortValue,
    });

  const filterFields: EntityFilterSelectField[] = [
    {
      id: "type",
      label: "Type",
      value: filterTab === "all" ? "" : filterTab,
      allLabel: "All types",
      options: TYPE_FILTER_TABS.filter((t) => t !== "all").map((t) => ({
        value: t,
        label: TYPE_LABELS[t],
      })),
      onChange: (v) => setFilterTab((v || "all") as ProviderTypeFilter),
    },
    {
      id: "status",
      label: "Status",
      value: statusFilter === "all" ? "" : statusFilter,
      allLabel: "All status",
      options: [
        { value: "enabled", label: "Enabled" },
        { value: "disabled", label: "Disabled" },
      ],
      onChange: (v) => setStatusFilter((v || "all") as ProviderStatusFilter),
    },
  ];

  const toggleControl = (provider: ProviderConfig) => (
    <label
      className="relative inline-flex cursor-pointer items-center"
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="checkbox"
        checked={provider.enabled}
        onChange={() =>
          toggleMutation.mutate({
            id: provider.providerId,
            enabled: !provider.enabled,
          })
        }
        className="peer sr-only"
      />
      <div className="h-5 w-9 rounded-full border border-border bg-muted after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-foreground after:transition-all peer-checked:bg-brand peer-checked:after:translate-x-full peer-checked:after:bg-brand-foreground" />
      <span className="ml-2 text-xs text-muted-foreground">
        {provider.enabled ? "Enabled" : "Disabled"}
      </span>
    </label>
  );

  const columns: Column<ProviderConfig & { id: string }>[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      render: (p) => (
        <span className="font-medium text-foreground">{p.name}</span>
      ),
    },
    {
      key: "type",
      header: "Type",
      sortable: true,
      render: (p) => (
        <span
          className={cn(
            "rounded-full px-2 py-0.5 text-[10px] font-medium",
            TYPE_COLORS[p.type],
          )}
        >
          {TYPE_LABELS[p.type]}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (p) => toggleControl(p),
    },
  ];

  const catalogEmpty = !isLoading && !error && providers.length === 0;
  const showNoMatch =
    !isLoading &&
    !error &&
    providers.length > 0 &&
    filteredProviders.length === 0;

  return (
    <div className="space-y-6">
      <header className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">Providers</h1>
        <p className="text-muted-foreground">
          Manage the shared provider catalog and global availability. To choose
          which boards are scanned for your account, go to{" "}
          <Link to="/explore?mode=boards" className="text-brand hover:underline">
            Portals → Providers
          </Link>
          .
        </p>
      </header>

      {!isLoading && !error && (useServerCursor || providers.length > 0) && (
        <div>
          <CollectionCommandBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search by name, description, or ID…"
            sortOption={sortOption}
            onSortChange={setSortOption}
            sortOptions={SORT_OPTIONS}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            allowedModes={allowedModes}
            filterFields={filterFields}
            activeFilterCount={
              (filterTab !== "all" ? 1 : 0) + (statusFilter !== "all" ? 1 : 0)
            }
            onClear={() => {
              setSearchQuery("");
              setFilterTab("all");
              setStatusFilter("all");
            }}
          />
          {!useServerCursor && (
            <p className="mt-2 text-xs text-muted-foreground">
              {filteredProviders.length === 0
                ? `0 of ${providers.length} (${enabledCount} enabled)`
                : `Showing ${clientPag.showingFrom}–${clientPag.showingTo} of ${filteredProviders.length} (${enabledCount} enabled)`}
            </p>
          )}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
        </div>
      )}
      {error && <p className="text-sm text-destructive">Failed to load providers.</p>}

      {catalogEmpty && (
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">No providers configured.</p>
        </div>
      )}

      {showNoMatch && (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            No providers match your filters.
          </p>
        </div>
      )}

      {!isLoading && !error && pageItems.length > 0 && (
        <>
          <EntityListLayout
            viewMode={viewMode}
            cards={
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {pageItems.map((provider) => (
                  <EntityCard
                    key={provider.providerId}
                    id={provider.providerId}
                    title={provider.name}
                    description={provider.description || provider.providerId}
                    primaryBadge={{
                      label: TYPE_LABELS[provider.type],
                      colorClass: TYPE_COLORS[provider.type],
                    }}
                    secondaryBadge={
                      isScannableAtsProvider(provider) ||
                      isScannableBoardProvider(provider)
                        ? {
                            label: isScannableAtsProvider(provider)
                              ? "Company Scan"
                              : "Board Scan",
                            colorClass: "bg-brand/10 text-brand",
                          }
                        : undefined
                    }
                    className={provider.enabled ? "" : "opacity-60"}
                    footerContent={toggleControl(provider)}
                    actions={
                      <Link
                        to={`/settings/providers/${provider.providerId}`}
                        className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground transition hover:text-foreground"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Details
                      </Link>
                    }
                    onClick={() =>
                      navigate(`/settings/providers/${provider.providerId}`)
                    }
                  />
                ))}
              </div>
            }
            list={
              <div className="space-y-2">
                {pageItems.map((provider) => (
                  <EntityCompactItem
                    key={provider.providerId}
                    id={provider.providerId}
                    title={provider.name}
                    description={provider.description || provider.providerId}
                    badges={[
                      {
                        label: TYPE_LABELS[provider.type],
                        colorClass: TYPE_COLORS[provider.type],
                      },
                    ]}
                    className={provider.enabled ? "" : "opacity-60"}
                    actions={
                      <>
                        {toggleControl(provider)}
                        <Link
                          to={`/settings/providers/${provider.providerId}`}
                          className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Details
                        </Link>
                      </>
                    }
                    onClick={() =>
                      navigate(`/settings/providers/${provider.providerId}`)
                    }
                  />
                ))}
              </div>
            }
            table={
              <DataTable
                data={sortedItems}
                columns={columns}
                onRowClick={(p) =>
                  navigate(`/settings/providers/${p.providerId}`)
                }
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            }
          />
          {useServerCursor ? (
            <PaginationBar
              mode="cursor"
              page={cursorPag.page}
              pageSize={cursorPag.pageSize}
              itemCount={pageItems.length}
              showingFrom={
                pageItems.length === 0 ? 0 : cursorPag.showingFrom
              }
              showingTo={cursorPag.showingTo(pageItems.length)}
              hasMore={cursorPag.hasMore}
              canPrev={cursorPag.canPrev}
              onFirst={cursorPag.goFirst}
              onPrev={cursorPag.goPrev}
              onNext={cursorPag.goNext}
              onPageSizeChange={cursorPag.setPageSize}
            />
          ) : (
            <PaginationBar
              page={clientPag.page}
              pageSize={clientPag.pageSize}
              totalItems={filteredProviders.length}
              totalPages={clientPag.totalPages}
              showingFrom={clientPag.showingFrom}
              showingTo={clientPag.showingTo}
              onPageChange={clientPag.setPage}
              onPageSizeChange={clientPag.setPageSize}
            />
          )}
        </>
      )}
    </div>
  );
}
